import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { Store as StoreIcon, Language as LanguageIcon, Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';

import { StoreFormData } from './types/store.types';

interface StoreBasicInfoFormProps {
  slugError?: string | null;
  setSlugError?: (error: string | null) => void;
  checking?: boolean;
  setChecking?: (checking: boolean) => void;
}

const StoreBasicInfoForm: React.FC<StoreBasicInfoFormProps> = ({
  slugError,
  setSlugError,
  checking,
  setChecking,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<StoreFormData>();

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Categorias de loja
  const storeCategories = [
    { value: 'general', label: 'Geral' },
    { value: 'fashion', label: 'Moda e Vestuário' },
    { value: 'electronics', label: 'Eletrônicos' },
    { value: 'home', label: 'Casa e Decoração' },
    { value: 'beauty', label: 'Beleza e Saúde' },
    { value: 'sports', label: 'Esportes' },
    { value: 'books', label: 'Livros e Mídia' },
    { value: 'toys', label: 'Brinquedos' },
    { value: 'automotive', label: 'Automotivo' },
    { value: 'food', label: 'Alimentação' },
    { value: 'art', label: 'Arte e Artesanato' },
    { value: 'religious', label: 'Religioso' },
    { value: 'other', label: 'Outros' },
  ];

  // Estados do Brasil
  const brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <StoreIcon color="primary" />
        Informações Básicas da Loja
      </Typography>

      <Grid container spacing={3}>
        {/* Nome da Loja */}
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Nome da Loja"
            placeholder="Ex: Minha Loja Online"
            {...register('name')}
            error={!!errors.name}
            helperText={errors.name?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StoreIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Categoria */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.category}>
            <InputLabel>Categoria</InputLabel>
            <Select
              label="Categoria"
              {...register('category')}
            >
              {storeCategories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
            {errors.category && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.category.message}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Slug */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="URL da Loja (slug)"
            placeholder="minha-loja-online"
            {...register('slug')}
            error={!!errors.slug || !!slugError}
            helperText={
              errors.slug?.message || 
              slugError || 
              'Esta será a URL da sua loja (ex: meusacramento.online/minha-loja-online)'
            }
            InputProps={{
              endAdornment: checking && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              setSlugManuallyEdited(true);
              register('slug').onChange(e);
            }}
          />
          {slugError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {slugError}
            </Alert>
          )}
        </Grid>

        {/* Descrição */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Descrição da Loja"
            placeholder="Descreva brevemente sua loja, os produtos que você vende e o que a torna especial..."
            {...register('description')}
            error={!!errors.description}
            helperText={errors.description?.message}
          />
        </Grid>

        {/* Website */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Website (opcional)"
            placeholder="https://www.meusite.com"
            {...register('website')}
            error={!!errors.website}
            helperText={errors.website?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Local */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Local da Loja"
            placeholder="Ex: Centro Comercial, Shopping, etc."
            {...register('location')}
            error={!!errors.location}
            helperText={errors.location?.message}
          />
        </Grid>

        {/* Endereço */}
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Endereço"
            placeholder="Rua, Avenida, etc."
            {...register('address')}
            error={!!errors.address}
            helperText={errors.address?.message}
          />
        </Grid>

        {/* CEP */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="CEP"
            placeholder="00000-000"
            {...register('zipCode')}
            error={!!errors.zipCode}
            helperText={errors.zipCode?.message}
          />
        </Grid>

        {/* Cidade */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Cidade"
            {...register('city')}
            error={!!errors.city}
            helperText={errors.city?.message}
          />
        </Grid>

        {/* Estado */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.state}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              {...register('state')}
            >
              {brazilianStates.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
            {errors.state && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.state.message}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Contato */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
            Informações de Contato
          </Typography>
        </Grid>

        {/* Email */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email de Contato"
            placeholder="contato@minhaloja.com"
            type="email"
            {...register('contactEmail')}
            error={!!errors.contactEmail}
            helperText={errors.contactEmail?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Telefone */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Telefone de Contato"
            placeholder="(11) 99999-9999"
            {...register('contactPhone')}
            error={!!errors.contactPhone}
            helperText={errors.contactPhone?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* Dicas e informações */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom color="primary">
          💡 Dicas para sua loja:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Escolha um nome atrativo e fácil de lembrar<br />
          • Descreva claramente o que sua loja oferece<br />
          • Use um slug simples e memorável para a URL<br />
          • Mantenha suas informações de contato sempre atualizadas
        </Typography>
      </Box>
    </Box>
  );
};

export default StoreBasicInfoForm;
